import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API, Auth } from 'aws-amplify';
import { getCachedAuthUser } from '../../utils/cachedAuth';
import { AppDispatch, RootState } from '../store';
import { Notification } from '../../types/notification';
import { RequestStatusObject, buildReducers, createRequestStatusObject } from '../utils';

interface NotificationsState {
	notifications: Notification[];
	shared_wallet?: any;
	byCategory: { [category: string]: Notification[] };
	listRequest: RequestStatusObject;
	listSharedWalletNotifications: RequestStatusObject;
	deleteRequest: RequestStatusObject;
}

const initialState: NotificationsState = {
	notifications: [],
	shared_wallet: null,
	byCategory: {},
	listRequest: createRequestStatusObject(),
	listSharedWalletNotifications: createRequestStatusObject(),
	deleteRequest: createRequestStatusObject()
};

export const listNotifications = createAsyncThunk<
	Notification[],
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('notifications/listNotifications', async () => {
	const u = await getCachedAuthUser();
	const response = await API.get('inspirusapi', `/users/${u.attributes.sub}/notifications`, {
		headers: {}
	});

	return response.items;
});

export const listSharedWalletNotifications = createAsyncThunk<
	any,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('notifications/listSharedWalletNotifications', async () => {
	const u = await getCachedAuthUser();
	const response = await API.get('inspirusapi', `/users/${u.attributes.sub}/shared-wallet-notifications`, {
		headers: {}
	});

	return response;
});

/**
 * Returns the ids it removed so the reducer can drop them from state.
 *
 * Without this the list is only correct again after the next fetch, which is fine for the
 * callers that clear on page entry but not for a dismiss button, where the row has to go at
 * the moment it is clicked.
 */
export const deleteNotifications = createAsyncThunk<string[], string[], { dispatch: AppDispatch; state: RootState }>(
	'notifications/deleteNotifications',
	async (notificationIds) => {
		const u = await Auth.currentAuthenticatedUser();
		for (const id of notificationIds) {
			await API.del('inspirusapi', `/users/${u.attributes.sub}/notifications`, {
				body: { id }
			});
		}
		return notificationIds;
	}
);

const notificationsSlice = createSlice({
	name: 'notifications',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		buildReducers(builder, listNotifications, 'listRequest', {
			onFulfilled: (state, payload) => {
				state.notifications = payload;
				state.byCategory = {};
				for (const notification of payload) {
					if (!state.byCategory[notification.category]) {
						state.byCategory[notification.category] = [];
					}
					state.byCategory[notification.category].push(notification);
				}
			}
		});
		buildReducers(builder, listSharedWalletNotifications, 'listSharedWalletNotifications', {
			onFulfilled: (state, payload) => {
				state.shared_wallet = payload?.totalCount;
			}
		});
		buildReducers(builder, deleteNotifications, 'deleteRequest', {
			onFulfilled: (state, payload) => {
				const removed = new Set(payload);
				state.notifications = (state.notifications ?? []).filter(
					(notification: Notification) => !removed.has(notification.id)
				);
				state.byCategory = {};
				for (const notification of state.notifications) {
					if (!state.byCategory[notification.category]) {
						state.byCategory[notification.category] = [];
					}
					state.byCategory[notification.category].push(notification);
				}
			}
		});
	}
});

export default notificationsSlice.reducer;
